import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { clientNotesApi, type ClientNote } from '@/api/client-notes';

export function useClientNotes(clientId: number | undefined) {
  return useQuery<ClientNote[]>({
    queryKey: ['client-notes', clientId],
    queryFn: () => clientNotesApi.getNotes(clientId!),
    enabled: !!clientId,
    staleTime: 30_000,
  });
}

export function useCreateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, text }: { clientId: number; text: string }) =>
      clientNotesApi.createNote(clientId, text),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['client-notes', clientId] });
    },
    onError: () => {
      toast.error('Ошибка создания заметки');
    },
  });
}

export function useUpdateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      noteId,
      text,
    }: {
      clientId: number;
      noteId: string;
      text: string;
    }) => clientNotesApi.updateNote(clientId, noteId, text),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['client-notes', clientId] });
    },
    onError: () => {
      toast.error('Ошибка обновления заметки');
    },
  });
}

export function useArchiveNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, noteId }: { clientId: number; noteId: string }) =>
      clientNotesApi.archiveNote(clientId, noteId),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['client-notes', clientId] });
    },
    onError: () => {
      toast.error('Ошибка архивации заметки');
    },
  });
}

export function useUploadImages() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      noteId,
      files,
    }: {
      clientId: number;
      noteId: string;
      files: File[];
    }) => clientNotesApi.uploadImages(clientId, noteId, files),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['client-notes', clientId] });
    },
    onError: () => {
      toast.error('Ошибка загрузки изображений');
    },
  });
}

export function useDeleteImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      noteId,
      imageId,
    }: {
      clientId: number;
      noteId: string;
      imageId: string;
    }) => clientNotesApi.deleteImage(clientId, noteId, imageId),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['client-notes', clientId] });
    },
    onError: () => {
      toast.error('Ошибка удаления изображения');
    },
  });
}
